import { useRef } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { useIsOverflowing } from '@atom/hooks/useIsOverflowing';
import { Tooltip } from '@atom/mui';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
// TODO(AM-18745): remove toggle
import { showDevelopmentFeature } from '@atom/utilities/featureToggleUtilities';
import timeUtilities from '@atom/utilities/timeUtilities';

import classes from './header.module.css';

interface Props {
  inventoryAssetDetail: InventoryAssetDetailType;
}

const HeaderFields = ({ inventoryAssetDetail }: Props) => {
  const container = useRef<HTMLDivElement>(null);

  const inventoryAssetName = inventoryAssetDetail.name || '-';
  const assetType = inventoryAssetDetail.assetType || '-';
  const address = inventoryAssetDetail.address || '-';
  const breadcrumbs =
    // @ts-ignore
    inventoryAssetDetail?.categories || [];

  const cost = (
    <span className={classes.costText}>{`${numberToLocaleString(
      R.pathOr('', ['budget', 'rate'], inventoryAssetDetail),
    )}/${R.pathOr('', ['budget', 'unit'], inventoryAssetDetail)}`}</span>
  );

  const breadcrumbString = breadcrumbs.map(folder => folder.name).join(' > ');
  const overflowing = useIsOverflowing(container, [breadcrumbString]);

  const folderCellContent = !overflowing ? (
    breadcrumbString
  ) : (
    <Tooltip title={breadcrumbString}>
      <span>{breadcrumbString}</span>
    </Tooltip>
  );

  if (showDevelopmentFeature) {
    const hasLocation = !R.isNil(inventoryAssetDetail.locationType);
    const hasCost = !!inventoryAssetDetail.isMaterial;
    const lastUpdated = timeUtilities.getUtcDisplayDate(
      inventoryAssetDetail.updatedDate,
    );

    return (
      <div className={classes.headerGrid}>
        <div className={classes.headerGridLabel}>Inventory Name:</div>
        <div className={classes.headerGridValue}>{inventoryAssetName}</div>
        <div className={classes.headerGridLabel}>Type:</div>
        <div className={classes.headerGridValue}>{assetType}</div>
        <div className={classes.headerGridLabel}>Inventory Folder:</div>
        <Tooltip title={overflowing ? breadcrumbString : ''}>
          <div ref={container} className={classes.headerGridFolderValue}>
            {breadcrumbString}
          </div>
        </Tooltip>
        <div className={classes.headerGridLabel}>Last Updated:</div>
        <div className={classes.headerGridValue}>{lastUpdated}</div>
        {hasLocation && (
          <>
            <div className={classes.headerGridLabel}>Location:</div>
            <div className={classes.headerGridValue}>{address}</div>
          </>
        )}
        {hasCost && (
          <>
            <div className={classes.headerGridLabel}>Cost:</div>
            <div className={classes.headerGridValue}>{cost}</div>
          </>
        )}
      </div>
    );
  }

  const headerItemText = inventoryAssetDetail.isMaterial
    ? 'Cost:'
    : 'Location:';
  const headerItemContent = inventoryAssetDetail.isMaterial ? cost : address;

  return (
    <div className={classes.infoBoxesWrapper}>
      <div className={clsx(classes.infoBox, classes.label)}>
        <div>Inventory Name:</div>
        <div>Last Updated:</div>
      </div>
      <div className={clsx(classes.infoBox, classes.value)}>
        <div>{inventoryAssetName}</div>
        <div>
          {timeUtilities.getUtcDisplayDate(inventoryAssetDetail.updatedDate)}
        </div>
      </div>
      <div className={clsx(classes.infoBox, classes.label)}>
        <div>Type:</div>
        <div>{headerItemText}</div>
      </div>
      <div className={clsx(classes.infoBox, classes.value)}>
        <div>{assetType}</div>
        <div>{headerItemContent}</div>
      </div>
      <div className={classes.breadcrumbsLabel}>Inventory Folder:</div>
      <div
        className={clsx(classes.infoBox, classes.breadcrumbs)}
        ref={container}
      >
        {folderCellContent}
      </div>
    </div>
  );
};

export default HeaderFields;
